export const SPATIAL_PROMPT_IDLE_DELAY_MS = 7_000;
export const SPATIAL_CONTROL_DEMO_LOOP_MS = 18_800;
export const SPATIAL_CONTROL_TOUCH_DEMO_LOOP_MS = 15_200;
export const SPATIAL_CONTROL_DEMO_MOVE_OFFSET_M = 0.52;
export const SPATIAL_CONTROL_DEMO_TOUCH_MOVE_OFFSET_M = 0.74;
export const SPATIAL_CONTROL_DEMO_LOOK_OFFSET_DEG = 9;
export const SPATIAL_CONTROL_DEMO_TOUCH_LOOK_OFFSET_DEG = 17;
export const SPATIAL_CONTROL_PRESENTATION_INTERVAL_MS = 1000 / 30;

export type SpatialControlDemoState = 'hidden' | 'running';
export type SpatialControlDemoStage =
  | 'idle'
  | 'moveIntro'
  | 'move'
  | 'lookIntro'
  | 'look'
  | 'summary';
export type SpatialControlDemoAction =
  | 'none'
  | 'forward'
  | 'back'
  | 'left'
  | 'right'
  | 'mouseDown'
  | 'lookLeft'
  | 'lookRight'
  | 'lookUp'
  | 'lookDown';

export interface SpatialPromptVisibilityInput {
  worldReady: boolean;
  entranceActive: boolean;
  toolActive: boolean;
  hasUserInteracted: boolean;
  nowMs: number;
  readySinceMs: number;
  lastInteractionMs: number;
  delayMs?: number;
}

export interface SpatialControlDemoStateInput extends SpatialPromptVisibilityInput {
  shownAtMs: number;
  loopMs?: number;
}

export interface SpatialControlDemoActionInput {
  touchMode: boolean;
  elapsedMs: number;
  loopMs?: number;
}

export interface SpatialControlDemoActionSample {
  action: SpatialControlDemoAction;
  stage: SpatialControlDemoStage;
  instruction: string;
  progress: number;
  strength: number;
  travel: number;
  slideIndex: number;
  slideCount: number;
}

export interface SpatialControlDemoTouchThumbOffset {
  moveX: number;
  moveY: number;
  lookX: number;
  lookY: number;
}

export interface SpatialControlDemoMouseOffset {
  x: number;
  y: number;
}

export interface SpatialControlPromptTickResult {
  visible: boolean;
  visibleChanged: boolean;
  demoState: SpatialControlDemoState;
  demoAction: SpatialControlDemoActionSample;
}

interface DemoSegment {
  action: SpatialControlDemoAction;
  stage: SpatialControlDemoStage;
  startMs: number;
  durationMs: number;
  instruction?: string;
}

const DESKTOP_MOVE_INSTRUCTION = 'WASD keys move you through the space';
const DESKTOP_LOOK_INSTRUCTION = 'Mouse changes your view';

const DESKTOP_DEMO_SEGMENTS: readonly DemoSegment[] = [
  {
    action: 'none',
    stage: 'move',
    startMs: 0,
    durationMs: 1_400,
    instruction: DESKTOP_MOVE_INSTRUCTION,
  },
  {
    action: 'forward',
    stage: 'move',
    startMs: 1_400,
    durationMs: 1_600,
    instruction: DESKTOP_MOVE_INSTRUCTION,
  },
  {
    action: 'back',
    stage: 'move',
    startMs: 3_000,
    durationMs: 1_600,
    instruction: DESKTOP_MOVE_INSTRUCTION,
  },
  {
    action: 'left',
    stage: 'move',
    startMs: 4_900,
    durationMs: 1_600,
    instruction: DESKTOP_MOVE_INSTRUCTION,
  },
  {
    action: 'right',
    stage: 'move',
    startMs: 6_500,
    durationMs: 1_600,
    instruction: DESKTOP_MOVE_INSTRUCTION,
  },
  {
    action: 'none',
    stage: 'move',
    startMs: 8_100,
    durationMs: 800,
    instruction: DESKTOP_MOVE_INSTRUCTION,
  },
  {
    action: 'mouseDown',
    stage: 'look',
    startMs: 8_900,
    durationMs: 1_100,
    instruction: DESKTOP_LOOK_INSTRUCTION,
  },
  {
    action: 'lookLeft',
    stage: 'look',
    startMs: 10_000,
    durationMs: 1_500,
    instruction: DESKTOP_LOOK_INSTRUCTION,
  },
  {
    action: 'lookRight',
    stage: 'look',
    startMs: 11_500,
    durationMs: 1_700,
    instruction: DESKTOP_LOOK_INSTRUCTION,
  },
  {
    action: 'lookUp',
    stage: 'look',
    startMs: 13_200,
    durationMs: 1_500,
    instruction: DESKTOP_LOOK_INSTRUCTION,
  },
  {
    action: 'lookDown',
    stage: 'look',
    startMs: 14_700,
    durationMs: 1_800,
    instruction: DESKTOP_LOOK_INSTRUCTION,
  },
  {
    action: 'none',
    stage: 'look',
    startMs: 16_500,
    durationMs: 2_300,
    instruction: DESKTOP_LOOK_INSTRUCTION,
  },
];

const TOUCH_DEMO_SEGMENTS: readonly DemoSegment[] = [
  { action: 'forward', stage: 'move', startMs: 600, durationMs: 1_500 },
  { action: 'back', stage: 'move', startMs: 2_100, durationMs: 1_500 },
  { action: 'left', stage: 'move', startMs: 3_600, durationMs: 1_500 },
  { action: 'right', stage: 'move', startMs: 5_100, durationMs: 1_500 },
  { action: 'lookLeft', stage: 'look', startMs: 7_000, durationMs: 1_400 },
  { action: 'lookRight', stage: 'look', startMs: 8_400, durationMs: 1_400 },
  { action: 'lookUp', stage: 'look', startMs: 9_800, durationMs: 1_400 },
  { action: 'lookDown', stage: 'look', startMs: 11_200, durationMs: 1_400 },
];

const INACTIVE_DEMO_ACTION: SpatialControlDemoActionSample = {
  action: 'none',
  stage: 'idle',
  instruction: '',
  progress: 0,
  strength: 0,
  travel: 0,
  slideIndex: 0,
  slideCount: 0,
};

export function shouldShowSpatialPrompt(input: SpatialPromptVisibilityInput): boolean {
  if (!input.worldReady || input.entranceActive || input.toolActive || input.hasUserInteracted) {
    return false;
  }
  const delayMs = input.delayMs ?? SPATIAL_PROMPT_IDLE_DELAY_MS;
  const idleBaseMs = Math.max(input.readySinceMs, input.lastInteractionMs);
  return input.nowMs - idleBaseMs >= delayMs;
}

export function resolveSpatialControlDemoState(
  input: SpatialControlDemoStateInput,
): SpatialControlDemoState {
  if (!shouldShowSpatialPrompt(input)) return 'hidden';
  return 'running';
}

function clamp01(value: number): number {
  return Math.min(1, Math.max(0, value));
}

function pressStrength(progress: number): number {
  const clamped = clamp01(progress);
  if (clamped < 0.1) return clamped / 0.1;
  if (clamped > 0.96) return (1 - clamped) / 0.04;
  return 1;
}

function smoothTravel(progress: number): number {
  const clamped = clamp01(progress);
  return clamped * clamped * (3 - 2 * clamped);
}

function slideIndexForStage(stage: SpatialControlDemoStage): number {
  if (stage === 'move' || stage === 'moveIntro') return 1;
  if (stage === 'look' || stage === 'lookIntro') return 2;
  return 0;
}

function resolveSegmentAction(
  segment: DemoSegment,
  elapsedMs: number,
  scale = 1,
): SpatialControlDemoActionSample | null {
  const durationMs = segment.durationMs * scale;
  const elapsedInSegmentMs = elapsedMs - segment.startMs * scale;
  if (elapsedInSegmentMs < 0 || elapsedInSegmentMs >= durationMs) return null;
  const progress = clamp01(elapsedInSegmentMs / durationMs);
  return {
    action: segment.action,
    stage: segment.stage,
    instruction: segment.instruction ?? '',
    progress,
    strength:
      segment.action === 'none' || segment.action === 'mouseDown' ? 0 : pressStrength(progress),
    travel:
      segment.action === 'none' || segment.action === 'mouseDown' ? 0 : smoothTravel(progress),
    slideIndex: slideIndexForStage(segment.stage),
    slideCount: 2,
  };
}

function mix(from: number, to: number, progress: number): number {
  return from + (to - from) * progress;
}

export function resolveSpatialControlDemoTouchThumbOffset(
  sample: SpatialControlDemoActionSample,
): SpatialControlDemoTouchThumbOffset {
  const travel = sample.travel;
  const moveOffset = 1.55;
  const look = {
    leftX: -1.58,
    leftY: -0.72,
    rightX: 1.58,
    rightY: 0.62,
    upX: 0.68,
    upY: -1.66,
    downX: -0.68,
    downY: 1.58,
  };

  switch (sample.action) {
    case 'forward':
      return { moveX: 0, moveY: mix(0, -moveOffset, travel), lookX: 0, lookY: 0 };
    case 'back':
      return { moveX: 0, moveY: mix(-moveOffset, moveOffset, travel), lookX: 0, lookY: 0 };
    case 'left':
      return {
        moveX: mix(0, -moveOffset, travel),
        moveY: mix(moveOffset, 0, travel),
        lookX: 0,
        lookY: 0,
      };
    case 'right':
      return { moveX: mix(-moveOffset, moveOffset, travel), moveY: 0, lookX: 0, lookY: 0 };
    case 'lookLeft':
      return {
        moveX: 0,
        moveY: 0,
        lookX: mix(0, look.leftX, travel),
        lookY: mix(0, look.leftY, travel),
      };
    case 'lookRight':
      return {
        moveX: 0,
        moveY: 0,
        lookX: mix(look.leftX, look.rightX, travel),
        lookY: mix(look.leftY, look.rightY, travel),
      };
    case 'lookUp':
      return {
        moveX: 0,
        moveY: 0,
        lookX: mix(look.rightX, look.upX, travel),
        lookY: mix(look.rightY, look.upY, travel),
      };
    case 'lookDown':
      return {
        moveX: 0,
        moveY: 0,
        lookX: mix(look.upX, look.downX, travel),
        lookY: mix(look.upY, look.downY, travel),
      };
    default:
      return { moveX: 0, moveY: 0, lookX: 0, lookY: 0 };
  }
}

export function resolveSpatialControlDemoMouseOffset(
  sample: SpatialControlDemoActionSample,
): SpatialControlDemoMouseOffset {
  const travel = sample.travel;
  const horizontalOffset = 3.35;
  const verticalOffset = 2.05;

  switch (sample.action) {
    case 'lookLeft':
      return { x: mix(0, -horizontalOffset, travel), y: 0 };
    case 'lookRight':
      return { x: mix(-horizontalOffset, horizontalOffset, travel), y: 0 };
    case 'lookUp':
      return { x: mix(horizontalOffset, 0, travel), y: mix(0, -verticalOffset, travel) };
    case 'lookDown':
      return { x: 0, y: mix(-verticalOffset, verticalOffset, travel) };
    default:
      return { x: 0, y: 0 };
  }
}

export function resolveSpatialControlDemoAction(
  input: SpatialControlDemoActionInput,
): SpatialControlDemoActionSample {
  const baseLoopMs = input.touchMode
    ? SPATIAL_CONTROL_TOUCH_DEMO_LOOP_MS
    : SPATIAL_CONTROL_DEMO_LOOP_MS;
  const loopMs = input.loopMs ?? baseLoopMs;
  if (input.elapsedMs < 0 || loopMs <= 0) return INACTIVE_DEMO_ACTION;

  const scale = loopMs / baseLoopMs;
  const elapsedInLoopMs = input.elapsedMs % loopMs;
  const segments = input.touchMode ? TOUCH_DEMO_SEGMENTS : DESKTOP_DEMO_SEGMENTS;
  for (const segment of segments) {
    const sample = resolveSegmentAction(segment, elapsedInLoopMs, scale);
    if (sample) return sample;
  }
  return INACTIVE_DEMO_ACTION;
}

export function shouldUpdateSpatialPromptPresentation(input: {
  visible: boolean;
  visibleChanged: boolean;
  nowMs: number;
  lastPresentationMs: number;
  intervalMs?: number;
}): boolean {
  if (!input.visible) return false;
  if (input.visibleChanged) return true;
  return (
    input.nowMs - input.lastPresentationMs >=
    (input.intervalMs ?? SPATIAL_CONTROL_PRESENTATION_INTERVAL_MS)
  );
}

export class SpatialControlPrompt {
  readonly root: HTMLDivElement;
  private readonly isTouchMode: boolean;
  private worldReady = false;
  private entranceActive = true;
  private toolActive = false;
  private hasUserInteracted = false;
  private visible = false;
  private readySinceMs = 0;
  private lastInteractionMs = 0;
  private shownAtMs = 0;
  private lastPresentationMs = Number.NEGATIVE_INFINITY;

  constructor(parent: HTMLElement, options: { touchMode: boolean }) {
    this.isTouchMode = options.touchMode;
    this.root = document.createElement('div');
    this.root.className = [
      'gii-spatial-prompt',
      options.touchMode ? 'gii-spatial-prompt--mobile' : 'gii-spatial-prompt--desktop',
    ].join(' ');
    this.root.setAttribute('aria-hidden', 'true');
    this.root.innerHTML = options.touchMode ? this.mobileMarkup() : this.desktopMarkup();
    parent.appendChild(this.root);
  }

  markInteraction(nowMs = performance.now()): void {
    this.hasUserInteracted = true;
    this.lastInteractionMs = nowMs;
    this.setVisible(false, nowMs);
  }

  setReady(worldReady: boolean, entranceActive: boolean, nowMs = performance.now()): void {
    this.entranceActive = entranceActive;
    if (this.worldReady === worldReady) return;
    this.worldReady = worldReady;
    if (worldReady) {
      this.readySinceMs = nowMs;
      this.lastInteractionMs = nowMs;
      this.hasUserInteracted = false;
      return;
    }
    this.readySinceMs = 0;
    this.lastInteractionMs = nowMs;
    this.hasUserInteracted = false;
    this.setVisible(false, nowMs);
  }

  setToolActive(active: boolean, nowMs = performance.now()): void {
    this.toolActive = active;
    if (active) this.setVisible(false, nowMs);
  }

  tick(nowMs = performance.now()): SpatialControlPromptTickResult {
    const demoState = resolveSpatialControlDemoState({
      worldReady: this.worldReady,
      entranceActive: this.entranceActive,
      toolActive: this.toolActive,
      hasUserInteracted: this.hasUserInteracted,
      nowMs,
      readySinceMs: this.readySinceMs,
      lastInteractionMs: this.lastInteractionMs,
      shownAtMs: this.shownAtMs,
    });
    const shouldShow = demoState !== 'hidden';
    const visibleChanged = this.setVisible(shouldShow, nowMs);
    const nextDemoState = this.visible ? demoState : 'hidden';
    const demoAction =
      nextDemoState === 'running'
        ? resolveSpatialControlDemoAction({
            touchMode: this.isTouchMode,
            elapsedMs: nowMs - this.shownAtMs,
          })
        : INACTIVE_DEMO_ACTION;

    if (
      shouldUpdateSpatialPromptPresentation({
        visible: this.visible,
        visibleChanged,
        nowMs,
        lastPresentationMs: this.lastPresentationMs,
      })
    ) {
      this.lastPresentationMs = nowMs;
      this.setDatasetValue('demoAction', demoAction.action);
      this.setDatasetValue('demoStage', demoAction.stage);
      this.setDatasetValue('demoSlide', String(demoAction.slideIndex));
      this.setDatasetValue('demoSlideCount', String(demoAction.slideCount));
      if (this.isTouchMode) {
        this.setTouchThumbOffsets(demoAction);
      } else {
        this.setMouseOffset(demoAction);
      }
    }
    return { visible: this.visible, visibleChanged, demoState: nextDemoState, demoAction };
  }

  isVisible(): boolean {
    return this.visible;
  }

  getShownAtMs(): number {
    return this.shownAtMs;
  }

  dispose(): void {
    this.root.remove();
  }

  private setVisible(visible: boolean, nowMs: number): boolean {
    if (this.visible === visible) return false;
    this.visible = visible;
    this.root.classList.toggle('gii-spatial-prompt--visible', visible);
    this.root.setAttribute('aria-hidden', String(!visible));
    if (visible) {
      this.shownAtMs = nowMs;
    }
    return true;
  }

  private setTouchThumbOffsets(sample: SpatialControlDemoActionSample): void {
    const thumb = resolveSpatialControlDemoTouchThumbOffset(sample);
    this.setStyleProperty('--gii-move-thumb-x', `${thumb.moveX.toFixed(3)}rem`);
    this.setStyleProperty('--gii-move-thumb-y', `${thumb.moveY.toFixed(3)}rem`);
    this.setStyleProperty('--gii-look-thumb-x', `${thumb.lookX.toFixed(3)}rem`);
    this.setStyleProperty('--gii-look-thumb-y', `${thumb.lookY.toFixed(3)}rem`);
  }

  private setMouseOffset(sample: SpatialControlDemoActionSample): void {
    const mouse = resolveSpatialControlDemoMouseOffset(sample);
    this.setStyleProperty('--gii-mouse-offset-x', `${mouse.x.toFixed(3)}rem`);
    this.setStyleProperty('--gii-mouse-offset-y', `${mouse.y.toFixed(3)}rem`);
  }

  private setDatasetValue(key: string, value: string): void {
    if (this.root.dataset[key] === value) return;
    this.root.dataset[key] = value;
  }

  private setStyleProperty(name: string, value: string): void {
    if (this.root.style.getPropertyValue(name) === value) return;
    this.root.style.setProperty(name, value);
  }

  private mobileMarkup(): string {
    return [
      '<div class="gii-spatial-panel gii-spatial-panel--mobile">',
      '<div class="gii-spatial-mobile-heading">',
      '<div class="gii-spatial-title">CONTROLS</div>',
      '<div class="gii-spatial-mobile-subtitle">Use your thumbs to control the view</div>',
      '</div>',
      '<div class="gii-spatial-mobile-pads" aria-hidden="true">',
      '<div class="gii-spatial-pad-cluster gii-spatial-pad-cluster--move">',
      '<span class="gii-spatial-pad-label">MOVE</span>',
      '<div class="gii-spatial-pad gii-spatial-pad--move">',
      '<span class="gii-spatial-pad-ring"></span>',
      '<span class="gii-spatial-pad-hotspot"></span>',
      '</div>',
      '</div>',
      '<div class="gii-spatial-pad-cluster gii-spatial-pad-cluster--look">',
      '<span class="gii-spatial-pad-label">LOOK</span>',
      '<div class="gii-spatial-pad gii-spatial-pad--look">',
      '<span class="gii-spatial-pad-ring"></span>',
      '<span class="gii-spatial-pad-hotspot"></span>',
      '</div>',
      '</div>',
      '</div>',
      '</div>',
    ].join('');
  }

  private desktopMarkup(): string {
    return [
      '<div class="gii-spatial-panel gii-spatial-panel--desktop">',
      '<div class="gii-spatial-title">CONTROLS</div>',
      '<div class="gii-spatial-instruction" aria-hidden="true">',
      '<span class="gii-spatial-instruction-line gii-spatial-instruction-line--move">WASD keys move you through the space</span>',
      '<span class="gii-spatial-instruction-line gii-spatial-instruction-line--look">Mouse changes your view</span>',
      '</div>',
      '<div class="gii-spatial-demo-stage">',
      '<span class="gii-spatial-keys" aria-hidden="true">',
      '<span class="gii-spatial-key gii-spatial-key--w">W</span>',
      '<span class="gii-spatial-key gii-spatial-key--a">A</span>',
      '<span class="gii-spatial-key gii-spatial-key--s">S</span>',
      '<span class="gii-spatial-key gii-spatial-key--d">D</span>',
      '</span>',
      '<span class="gii-spatial-mouse" aria-hidden="true"></span>',
      '</div>',
      '<div class="gii-spatial-page gii-spatial-page--move">Page 1 of 2</div>',
      '<div class="gii-spatial-page gii-spatial-page--look">Page 2 of 2</div>',
      '</div>',
    ].join('');
  }
}
